/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, forwardRef, useEffect } from "react";
import useEffectAsync, { useIsMounted } from "lib/react/customHooks";
import MaterialTable from "material-table";
import { Chip } from "@material-ui/core";
import {
  AddBox,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  Edit,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  SaveAlt,
  Search,
  ViewColumn
} from "@material-ui/icons";
import { partialUpdateAthenaTenant } from "lib/services/athenaTenants";
import { getRoles } from "lib/services/roles";
import {
  RolesAutocomplete,
  CompaniesAutocomplete
} from "app/utils/autocomplete";
import TenantCompanyLabel from "app/utils/TenantCompanyLabel";
import _ from "lodash";
import DeleteAthenaTenantComponent from "./DeleteAthenaTenantComponent";

// Defined at module scope (not inside AthenaTenantsTable) so they keep a stable
// identity across renders; nesting them would remount every cell and re-fire
// its role fetches whenever the table re-renders (e.g. while editing).

// Cache roles by tenant id so MaterialTable remounting the cells on each edit
// doesn't stampede the API, and guard the fetch with useIsMounted so a late
// response never updates an unmounted cell. Cleared when new tenant data
// arrives.
export const tenantRolesCache = new Map();

const AthenaTenantRoles = ({ athenaTenant }) => {
  const isMounted = useIsMounted();
  const [roles, setRoles] = useState(
    () => athenaTenant.roles || tenantRolesCache.get(athenaTenant.id) || []
  );

  useEffect(() => {
    if (athenaTenant.roles) {
      tenantRolesCache.set(athenaTenant.id, athenaTenant.roles);
      setRoles(athenaTenant.roles);
      return;
    }
    if (tenantRolesCache.has(athenaTenant.id)) {
      setRoles(tenantRolesCache.get(athenaTenant.id));
      return;
    }
    getRoles({ athena_tenant_id: athenaTenant.id })
      .then(response => {
        tenantRolesCache.set(athenaTenant.id, response.data);
        if (isMounted()) setRoles(response.data);
      })
      .catch(() => {
        /* leave roles empty on failure rather than crashing the cell */
      });
  }, [athenaTenant.id]);

  return (
    <div>
      {roles.map(role => (
        <Chip key={role.id} label={role.name} variant="outlined" />
      ))}
    </div>
  );
};

const AthenaTenantRolesAutocomplete = ({ athenaTenant, onChange }) => {
  const [roles, setRoles] = useState(athenaTenant.roles);

  useEffectAsync(
    async () => {
      const response = await getRoles({
        athena_tenant_id: athenaTenant.id
      });
      return response.data;
    },
    setRoles,
    []
  );

  return roles ? (
    <RolesAutocomplete selectedRoles={roles} onChange={onChange} />
  ) : null;
};

export default function AthenaTenantsTable({ refreshKey, athenaTenants }) {
  const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => (
      <ChevronRight {...props} ref={ref} />
    )),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => (
      <ChevronLeft {...props} ref={ref} />
    )),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => (
      <ArrowDownward {...props} ref={ref} />
    )),
    ThirdStateCheck: forwardRef((props, ref) => (
      <Remove {...props} ref={ref} />
    )),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />)
  };

  const [isLoading] = useState(false);
  const initialRefresh = 0;
  const [refresh, setRefresh] = useState(initialRefresh);

  useEffect(() => {
    // Do not update refresh key on initial mount
    if (refreshKey !== initialRefresh) setRefresh(refresh + 1);
  }, [refreshKey]);

  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false
  });

  const closeDeleteDialog = () => {
    setDeleteDialogState({ open: false });
  };

  const handleDeleteClick = (_event, athenaTenant) => {
    setDeleteDialogState({
      open: true,
      athenaTenant
    });
  };

  const refreshTable = () => {
    setRefresh(refresh + 1);
  };

  const onDeleteAthenaTenantSuccess = () => {
    refreshTable();
    closeDeleteDialog();
  };

  const [state, setState] = useState({
    columns: [
      {
        title: "ID",
        field: "id",
        width: 30,
        editable: "never"
      },
      {
        title: "Company",
        field: "company_id",
        render: rowData => <TenantCompanyLabel rowData={rowData} />,
        editComponent: props => {
          const { rowData, onChange } = props;
          return (
            <CompaniesAutocomplete
              selectedCompanyId={rowData.company_id}
              onChange={company => onChange(company ? company.id : null)}
            />
          );
        },
        width: 200,
        editable: "never"
      },
      {
        title: "MySQL User",
        field: "mysql_user",
        width: 100,
        editable: "never"
      },
      {
        title: "MySQL Port",
        field: "mysql_port",
        width: 30,
        editable: "never"
      },
      {
        title: "MySQL Database",
        field: "mysql_database",
        width: 100,
        editable: "never"
      },
      {
        title: "Customer MySQL Hostname",
        field: "mysql_host",
        editable: "never"
      },
      {
        title: "Roles",
        field: "roles",
        render: rowData => <AthenaTenantRoles athenaTenant={rowData} />,
        editComponent: props => {
          const { rowData, onChange } = props;
          return (
            <AthenaTenantRolesAutocomplete
              athenaTenant={rowData}
              onChange={onChange}
            />
          );
        },
        editable: "always"
      }
    ],
    data: []
  });

  useEffect(() => {
    // Fresh tenant data -> drop any stale per-row roles we had cached.
    tenantRolesCache.clear();
    // Insert the "Preview MySQL Hostname" column once, only if the data carries
    // that field. Use a functional update and a copied columns array so we never
    // mutate state in place, and set the data in a single update.
    setState(prev => {
      const columns = [...prev.columns];
      const hasPreview =
        athenaTenants.length !== 0 &&
        Object.prototype.hasOwnProperty.call(
          athenaTenants[0],
          "mysql_host_preview"
        );
      if (
        hasPreview &&
        columns[6] &&
        columns[6].title !== "Preview MySQL Hostname"
      ) {
        columns.splice(6, 0, {
          title: "Preview MySQL Hostname",
          field: "mysql_host_preview",
          editable: "never"
        });
      }
      return { ...prev, columns, data: athenaTenants };
    });
  }, [athenaTenants]);

  return (
    <>
      <MaterialTable
        title="Tenants"
        // material-table mutates the column objects it is given: on every data
        // update it rewrites column.tableData.width, wrapping the previous
        // value in another css calc(). Feeding it the same objects from state
        // makes that string grow exponentially, so hand it disposable clones
        // and keep ours pristine.
        columns={state.columns.map(column => ({ ...column }))}
        data={state.data}
        icons={tableIcons}
        isLoading={isLoading}
        options={{
          actionsColumnIndex: -1
        }}
        editable={{
          onRowUpdate: async (newData, oldData) => {
            const data = [...state.data];
            data[oldData.tableData.id] = newData;
            setState({
              ...state,
              data
            });

            const ignoreProps = {
              tableData: true,
              _links: true,
              company: true
            };
            const updateTenant = {};

            _.forEach(newData, (value, prop) => {
              if (newData[prop] !== oldData[prop] && !(prop in ignoreProps)) {
                switch (prop) {
                  case "roles":
                    updateTenant.role_ids = _.map(value, role => role.id);
                    break;
                  default:
                    updateTenant[prop] = newData[prop];
                }
              }
            });

            if (!_.isEmpty(updateTenant)) {
              partialUpdateAthenaTenant({
                id: newData.id,
                ...updateTenant
              });
            }
          }
        }}
        actions={[
          {
            icon: DeleteOutline,
            tooltip: "Delete Tenant",
            onClick: handleDeleteClick
          }
        ]}
      />
      <DeleteAthenaTenantComponent
        open={deleteDialogState.open}
        athenaTenant={deleteDialogState.athenaTenant}
        onSuccess={onDeleteAthenaTenantSuccess}
        onDismiss={closeDeleteDialog}
        // userPool={userPool}
      />
    </>
  );
}
